import type { components } from "api/generated/types";
import { IFitImages } from "api/fit/fit.type";

export interface IUtilsImageRemoveBgRequest {
  train_id?: string;
  image_id?: string;
  img_path: string;
}

export interface IUtilsImageDescribeRequest {
  train_id: string;
  image_id: string;
  img_path: string;
}

export interface IUtilsUploadPatternRequest {
  pattern_image: string;
}

export interface IUtilsContactUsRequest {
  first_name: string;
  last_name: string;
  email: string;
  company: string;
  company_size: string;
  message: string;
  type: string;
}

// Valid gender values for upload files (UPPERCASE)
export type UploadFileGender = "WOMAN" | "MAN" | "GIRL" | "BOY" | null;

// Angle classifications for product upload (back and side views)
export interface IProductAngles {
  back?: string;
  side?: string;
  // REF-7063 — a ROAR product's user-filled front. Never the cover: `object_path` stays ROAR's / the original.
  front?: string;
}

// Extra images for product upload (label and detail shots).
//
// REF-6194 — detail shots are split into three buckets so a back-only close-up (rear pocket, back yoke)
// stops being fed into the FRONT render. The BE routes them per generated view: front gets
// `detail` + `detail_front`, back gets `detail` + `detail_back`, side gets all three. `detail` keeps its
// original view-agnostic meaning, which is what makes the split non-breaking — it is still the right home
// for a shot that genuinely applies to both faces (a fabric macro, a trim close-up). BE ticket REF-6179.
//
// ⚠️ The edit endpoint REPLACES this whole object — `extra_images=%s` with no COALESCE, so an omitted key
// nulls the column and takes the label and every bucket with it. Every writer must round-trip the COMPLETE
// object, including the rename paths that only mean to change a name. `editUploadedFile` also drops the key
// when it looks empty, so that emptiness check has to consider all four buckets, not just `label`/`detail`.
// (The batch endpoint is the opposite — COALESCE, omitted means preserved.)
//
// REF-7104 — `logo` is the product's own logo images (the brand's mark, ideally on transparency), a LIST.
// It is not a view of the product: the BE cuts each one out and puts it back on every studio frame of the
// product (try-on and ghost / flat), so it stays out of the angle galleries and pickers. It is the fifth key
// every writer has to round-trip.
export interface IProductExtraImages {
  label?: string;
  detail?: string[];
  detail_front?: string[];
  detail_back?: string[];
  logo?: string[];
}

export interface IUtilsUploadFilesRequest {
  image_path: string;
  file_type: string;
  object_desc?: string;
  category?: string;
  /** @deprecated Use `angles` and `extra_images` instead */
  angle_urls?: string[];
  angles?: IProductAngles;
  extra_images?: IProductExtraImages;
  // Background angle frames — generated ONLY via the AI virtual studio "Create Angles with AI" flow
  // (REF-6157 locked-product decision; manual upload/replace/delete of angles was removed). Persisted on
  // the background asset's `view_urls` column and read back as `views` (BE MR !6559, shipped to dev). On
  // this POST edit endpoint this is a full-list rebuild — it re-processes every panel. Only sent for
  // file_type "background".
  view_image_urls?: string[];
  // Background-only (REF-6157 handover): true = run AI multi-angle "virtual studio" generation (20 credits);
  // false/omit = plain upload. The BE currently DEFAULTS an omitted value to true, so a plain single-background
  // upload must send `false` EXPLICITLY or it wrongly triggers generation. AI generation itself runs via the
  // dedicated `models/background_upload` flow (BackgroundGenerator); this upload only persists the result.
  create_virtual_studio?: boolean;
  gender?: UploadFileGender;
  use_case?: string;
  // Top-level try-on fit instruction (REF-5700). DISTINCT from `details.Fit` (DeFacto/PLM metadata).
  fit_prompt?: string;
  // Free-form reference to this asset's id in an external / partner system (REF-5934; BE side is
  // REF-5896). Passthrough only: the BE stores and echoes it back,
  // it drives no generation behaviour and costs no credit. A string or a number is accepted — the
  // column is TEXT and the BE trims whatever it gets to a string (`12345` → `"12345"`, `""` →
  // `NULL`), so reads always come back as a string.
  // ⚠️ On the EDIT endpoint this field is COALESCE-preserved — see `editUploadedFile`. Note `null`
  // is deliberately NOT accepted here: it cannot clear the stored value, so feed a read value back
  // as `external_id ?? undefined` rather than widening this type.
  external_id?: string | number;
}

// Batch register (POST /utils/upload_files/batch) — list-form of upload_files, up to 200 items in one atomic
// transaction. Lightweight: URLs are stored as-given (NO server-side orientation fix, thumbnail, or studio
// detection), so send clean, already-hosted URLs. Idempotent per user on external_id (re-POST updates the
// existing row(s) rather than duplicating). See REF batch-upload handover.
export interface IUtilsBatchUploadItem {
  image_path: string;
  external_id?: string | number;
  file_type?: string;
  category?: string;
  object_desc?: string;
  gender?: UploadFileGender;
  use_case?: string;
  // Per-item product angle views (back/side) + extra shots (label/detail). Stored as-given — send
  // already-hosted public URLs (batch does no server-side work). Mirrors the single-upload item.
  angles?: IProductAngles;
  extra_images?: IProductExtraImages;
  // Listing falls back to image_path when omitted (batch does not generate thumbnails).
  thumbnail_url?: string;
}

export interface IUtilsBatchUploadRequest {
  items: IUtilsBatchUploadItem[];
}

export interface IUtilsBatchUploadResponseItem {
  id: number;
  external_id?: string | null;
  // false ⇒ the row was updated (matched an existing external_id), not created.
  created: boolean;
}

export interface IUtilsBatchUploadResponse {
  count: number;
  created: number;
  updated: number;
  items: IUtilsBatchUploadResponseItem[];
}

export interface IUtilsGenerateUploadUrlResponse {
  upload_url: string;
  file_url: string;
  file_name: string;
  /**
   * Headers the PUT to `upload_url` MUST send back verbatim (REF-6408).
   *
   * Anything the backend passes to `generate_signed_url` becomes part of the V4 signature
   * (`X-Goog-SignedHeaders`), so a PUT that omits one does not merely skip a check — the
   * signature does not match and GCS answers 403 SignatureDoesNotMatch. Today this carries
   * `x-goog-content-length-range`, the upload size bound; the browser PUTs straight to the
   * bucket, so the signature is the only layer that can enforce it.
   *
   * OPTIONAL on purpose. Spread whatever arrives instead of naming the header here: that is
   * what lets this client work against a backend that sends it and one that does not, so
   * the two deploys do not have to be simultaneous.
   */
  upload_headers?: Record<string, string>;
  /** The same ceiling as a number, for a client-side pre-check. Never hardcode it. */
  max_upload_bytes?: number;
  message?: string;
}

// `POST utils/generate_resumable_upload_url` — a GCS RESUMABLE session for one large archive.
//
// The single-PUT signer above tops out where one request can reasonably carry a file; a Photoshoot
// Studio ZIP may be several GB, so it is sent in chunks against a session the BACKEND opens (it
// passes our Origin, so the chunk PUTs pass CORS, and `size_bytes`, so GCS itself refuses any other
// final length). Only `purpose: "studio_zip"` is accepted.
export interface IUtilsGenerateResumableUploadUrlRequest {
  file_name: string;
  content_type: string;
  size_bytes: number;
  purpose: "studio_zip";
}

export interface IUtilsGenerateResumableUploadUrlResponse {
  /** The session URI the chunks are PUT to. A bearer-like write handle — never log or send it anywhere. */
  session_url: string;
  /** Where the object is readable once complete (the CLEAN bucket on a scanned environment). */
  file_url: string;
  file_name: string;
  /** The server's ceiling for this purpose. The FE compares against it; it never hardcodes one. */
  max_upload_bytes: number;
  /** Chunk size the server wants, a multiple of 256 KiB. */
  chunk_size_bytes?: number;
}

// 400 `{ responseData: { error_code, message } }` codes (`upload.too_large` adds `max_upload_bytes`) of the endpoint above; 500 is `upload.session_failed`.
export type ResumableUploadErrorCode = "upload.invalid_purpose" | "upload.not_a_zip" | "upload.too_large" | "upload.size_required" | "upload.origin_not_allowed" | "upload.session_failed";

export interface IUtilsGetUploadedFilesParams {
  page?: number;
  limit?: number;
  gender?: UploadFileGender;
  category?: string;
  assetsType?: string;
  // REF-6429 — additive, default-off `object_type` exclusions for the mixed `all` listing. Sent as
  // REPEATED BARE KEYS (`?exclude_types=background&exclude_types=pose`); the service attaches
  // `serializeParams` whenever a param is an array, because axios' default `key[]=` form is dropped by
  // FastAPI and the request then 200s with an UNFILTERED list. Applied server-side BEFORE pagination, so
  // `page.itemsCount` describes the eligible rows. Omitting it leaves the request, the SQL and the returned
  // ROWS byte-identical — but the response BODY always carries `page.excludedTypes` now (`null` when
  // nothing was excluded), because that field lives on the BE's shared `Page` model.
  exclude_types?: string[];
  // NOTE: the BE also accepts `joor_status` here, but the asset lists deliberately don't surface a
  // JOOR filter (REF-5934) — an asset is an export INPUT, not a render, and a filter there implied it
  // could be exported. Not typed, so nobody wires it back by accident.
  [key: string]: any; // Allow additional params for backwards compatibility
}

export interface IUploadedFile {
  assets_id: number;
  object_id: string;
  object_path: string;
  thumbnail_url?: string;
  mask_path?: {
    top_garment?: string;
    low_garment?: string;
    dress_garment?: string;
  };
  object_type: string;
  object_desc?: string;
  is_sample?: boolean;
  category?: string;
  gender?: UploadFileGender;
  created_at?: string;
  updated_at?: string;
  details?: Record<string, string | number | null>;
  // Top-level try-on fit instruction (REF-5700). DISTINCT from `details.Fit` (DeFacto/PLM metadata).
  fit_prompt?: string;
  angle_classifications?: IProductAngles;
  extra_images?: IProductExtraImages;
  // External-system reference (REF-5934), returned by both GET utils/upload_file/{id} and each item
  // of GET utils/upload_files/{file_type}. Always a string on read; `null` when the asset has none.
  external_id?: string | null;
  // Free-form description (REF-6157 add-description). Written ONLY via the dedicated
  // `setUploadFileDescription` endpoint (owner-only) — the main edit endpoints never carry it. Returned by
  // GET utils/upload_file/{id}, GET /v1/assets, and GET /v1/history. `null` when unset/cleared.
  description?: string | null;
  // Virtual-studio background angle panels (REF-6157). `views` is the dense list [background, ...angles];
  // `source_image_url` is the original background to use as the main/front tile; `has_views` is the ONLY
  // reliable multi-angle indicator (`views` is empty for a flat background). See
  // `hooks/useUploadedFiles/types.ts`'s `UploadedFileItem` for the sibling feed-row shape.
  views?: string[];
  source_image_url?: string | null;
  has_views?: boolean;
  // Saved Fit Studio covers (REF-6476 FE / REF-6421 BE), added to the product-list row serializer. A
  // display-only preview surface — never folded into `extra_images`/`angle_classifications`
  // [ASSET_EXTRA_IMAGES_OMISSION] applies only to that field, not this one. `null` when the product has
  // no saved fits.
  fit_images?: IFitImages | null;
  // REF-7063 — GET utils/upload_file/{id} only (list rows do not carry it). `null` = not a ROAR product; an object = a
  // ROAR product, whose `locked_slots` were delivered by ROAR and are read-only for everyone, owner included. See
  // `utils/roarProductPolicy`.
  roar?: IRoarProductPolicy | null;
}

// Write the free-form asset/product description (owner-only). POST /v1/utils/upload_files/{assets_id}/description.
// Max 2000 chars. Sending "" or whitespace-only CLEARS the stored value (reads back as `null` afterwards).
// The BE returns HTTP 200 with `responseCode: 404` (+ `_handled: true`) for a non-owner / sample / missing
// asset rather than throwing — callers MUST check `res?._handled` before treating the call as successful
// (Known Trap #1).
export interface ISetUploadFileDescriptionRequest {
  assets_id: string | number;
  description: string;
}

export interface ISetUploadFileDescriptionResponse {
  description: string | null;
}

// REF-7063 — the slot endpoint's request comes from the BE's OpenAPI (`PartnerSlotRequest`). The five slots: `front`
// = `angle_classifications.front` on a ROAR product (a user's front never replaces the cover, `object_path`), `back` /
// `side` = `angle_classifications`, `label` = `extra_images.label`, `detail` = the detail shots (appends).
export type IUtilsSetUploadedFileSlotRequest = components["schemas"]["PartnerSlotRequest"];
export type RoarProductSlot = IUtilsSetUploadedFileSlotRequest["slot"];

// Hand-written: the BE declares neither `roar` nor the slot response in its OpenAPI (both answer the untyped
// `SuccessEnvelope`). Keep in step with `refabric-v3-api` until it does.
export interface IRoarProductPolicy {
  // Delivered by ROAR: read-only for everyone. Wins over `user_slots` — a later ROAR push overwrites a user's image.
  locked_slots: RoarProductSlot[];
  // Filled by a user (any team member who can see the product); replaceable through the slot endpoint.
  user_slots: RoarProductSlot[];
  // The product's attributes (name, type, gender, PLM details) cannot be edited.
  attributes_locked: boolean;
}

// POST /utils/upload_files/{assets_id}/slot → 200 `{ uploads_id, slot }`. 409 = the slot is ROAR-locked; 404 = not
// visible to the caller, or not a ROAR product; 422 = a url that is not public http(s).
export interface IUtilsSetUploadedFileSlotResponse {
  uploads_id: number | null;
  slot: RoarProductSlot;
}

// `POST /utils/upload_files/{assets_id}`. The deployed BE answers 200 with `uploads_id: null` when the row was not
// changed (not the caller's, or gone); the fixed BE answers 404 instead. Only `uploads_id === assets_id` is a save —
// `isUploadedFileEditConfirmed` (api/utils/uploadedFileEdit.ts) is the check (REF-7063).
export interface IUtilsEditUploadedFileResponse {
  uploads_id: number | null;
  object_id?: string | null;
}

export interface IUtilsGetUploadedFilesResponse {
  items: IUploadedFile[];
  total_rows: number;
}

export interface ITempModelPose {
  image_path: string;
  pose_id: number;
  description?: string;
}

export interface ITempModelPosesResponse {
  responseCode: number;
  responseMessage: string;
  responseData: ITempModelPose[];
}

export interface IAssetFilter {
  title: string;
  input_type: "select_box" | "input";
  // Nullable: the backend has been observed returning `values: null` for a
  // select-box filter (Sentry REFABRIC-FRONTEND-7591123611 — crashed the filter
  // bar). Consumers must guard with `?? []` before mapping.
  values: (string | number)[] | null;
}

export interface IAssetsFiltersResponse {
  [key: string]: IAssetFilter;
}

// ============ Bulk Download Types ============

export interface IBulkDownloadRequest {
  img_urls: string[];
  file_type: "svg" | "png" | "jpg";
}

// REF-5664 (Option C): fire-and-forget client-side download log ping. All optional except action;
// the caller sends whatever it has at the download site so backoffice download_logs captures the
// client-side downloads that never hit a BE endpoint.
export interface ILogDownloadRequest {
  action: "download_image" | "download_zip" | "download_svg" | "download_techpack";
  file_type?: string;
  resolution?: "2k" | "4k";
  design_id?: string;
  image_id?: string;
  image_url?: string;
  item_count?: number;
  surface?: string;
  // REF-6450: WHAT was downloaded, where `surface` says WHERE. Free text on the wire; the BE trims it,
  // drops blank or >64-char values to NULL, and stamps `sub_type_source='client'` itself (there is
  // deliberately no client-settable source field). Send a `StudioImageType` for Studio / Edit Design
  // frames — that vocabulary maps 1:1 onto the `result_data` keys the backoffice already labels — plus
  // `final_from_tryon` for a final that is the try-on output (see `getStudioDownloadSubType`), and
  // the node's `editMode` / `mode` for Design Canvas. Omit it rather than guess: an unknown label
  // renders as-is in the backoffice, a NULL falls back to its reconstruction.
  sub_type?: string;
}

// REF-5701: server-side download processor (POST utils/process-download). Called only when the
// user enables the "Include AI Generated" download toggle (a label-off png/jpg/webp download stays
// client-side). The BE burns an "AI Generated" pill + embeds AI-provenance metadata, re-encodes to
// file_type, and returns a public URL the FE saves.
export interface IProcessDownloadRequest {
  image_url: string;
  file_type: string;
  ai_label: boolean;
}

export interface IProcessDownloadResponse {
  download_url: string;
}

export interface IBulkDownloadResult {
  original_url: string;
  download_url: string | null;
  success: boolean;
  error?: string;
}

export interface IBulkDownloadResponse {
  results: IBulkDownloadResult[];
  message: string;
  total: number;
  successful: number;
}

// ============ Image Reupload Types ============

export interface IUtilsReuploadImageRequest {
  image_url: string;
}

export interface IUtilsReuploadImageResponse {
  cdn_url: string;
}

// ============ Reflat Image Types ============

export interface IUtilsReflatImageRequest {
  images: number[];
}

// ============ Astra SVG Conversion Queue (BE `astra-svg-default-pipeline`) ============
//
// The SVG engine is now gpt-6-astra: median 81 s, worst measured 283 s over 24 production
// drawings, so `download_tech_pack` can no longer answer synchronously for `file_type=svg`.
// It replies 202 with a job handle and the result is polled from `svg_convert_status/{job_id}`.
//
// ⚠️ The HTTP status is NOT observable here. `HttpClient.get` ends with `return response.data`,
// so a 202 reaches callers as the bare body, with no `_handled` flag, and `res.status` is the
// BODY's status string ("queued"), never 202. Discriminate by SHAPE — `isSvgConvertJobAccepted`
// / `isSvgConvertImmediateResult` below — never by an HTTP status code.
//
// The raster formats (png/jpg) still answer 200 + `download_url` synchronously, so callers resolve both
// shapes. The BE's former `SVG_ASTRA` kill switch was removed; SVG always queues.

/** Terminal-vs-intermediate job states. Anything NOT `done`/`failed` — including a value the BE
 *  adds later — counts as intermediate, per the BE contract. */
export type TSvgConvertStatus = "queued" | "processing" | "done" | "failed";

/** 202 body: the job was queued and credit was reserved. */
export interface ISvgConvertJobAccepted {
  job_id: string;
  status: string;
  /** Ready-made path, e.g. `/v1/utils/svg_convert_status/<id>`. Deliberately UNUSED: `RefabricProURL`'s
   *  base already ends in `/v1`, so replaying it verbatim would request `/v1/v1/utils/…`. The service
   *  builds the path from `job_id` instead. */
  status_url?: string;
  /** Credits reserved at request time. 20 today but explicitly a placeholder — read it, never hardcode it. */
  credit_reserved?: number;
}

/** Poll response. `download_url`/`editor_url` are populated only once `status === "done"`. */
export interface ISvgConvertStatusResponse {
  job_id: string;
  status: TSvgConvertStatus | string;
  download_url?: string;
  editor_url?: string;
  /** User-displayable failure text. Deliberately narrow BE-side (the worker's raw error is withheld
   *  because the distinction is a network-scanning signal); the job id is the way to get the detail. */
  error?: string;
  /** Diagnostics only — the old pipeline never emits these, so their presence proves the job ran on Astra. */
  areas_copied?: number;
  raster_area_frac?: number;
}

/** Synchronous body — the raster formats (png/jpg). */
export interface ISvgConvertImmediateResult {
  download_url: string;
  editor_url?: string;
}

/** Some utils bodies arrive bare and some wrapped in `responseData` (see `UploadGarmentImages`,
 *  which already reads both). Flatten before applying any shape guard. */
export const unwrapUtilsBody = <T>(res: any): T | undefined => {
  if (!res || typeof res !== "object") return undefined;

  return (res.responseData && typeof res.responseData === "object" ? res.responseData : res) as T;
};

const isNonEmptyString = (value: unknown): value is string => typeof value === "string" && value.trim().length > 0;

/** True for a queued (202) body. */
export const isSvgConvertJobAccepted = (body: any): body is ISvgConvertJobAccepted => isNonEmptyString(body?.job_id) && !isNonEmptyString(body?.download_url);

/** True for a legacy immediate body. Checked FIRST so a hypothetical body carrying both resolves now. */
export const isSvgConvertImmediateResult = (body: any): body is ISvgConvertImmediateResult => isNonEmptyString(body?.download_url);

/** `done` and `failed` are the only terminal states; an unknown value means "still going". */
export const isSvgConvertTerminal = (status: unknown): boolean => status === "done" || status === "failed";
